import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import {
  ReceivableDocumentType,
} from "../../utils/types";
import {
  documentFormSchema,
  DocumentFormValues,
} from "./utils";
import { DocumentFormFields } from "./DocumentFormFields";
import { DocumentFileUpload } from "./DocumentFileUpload";
import { DocumentListItem } from "./utils";

interface DocumentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: DocumentListItem | null;
  onSubmit: (values: DocumentFormValues) => Promise<void>;
  disabled?: boolean;
}

export function DocumentEditDialog({
  open,
  onOpenChange,
  document,
  onSubmit,
  disabled = false,
}: DocumentEditDialogProps) {
  const [fileInputKey, setFileInputKey] = useState(0);
  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      documentType: ReceivableDocumentType.INVOICE_FROM_PLATFORM,
      description: "",
      documentUrl: "",
      file: undefined,
    },
  });

  const isSubmitting = form.formState.isSubmitting;
  const selectedFile = form.watch("file");
  const documentUrl = form.watch("documentUrl");

  useEffect(() => {
    const hasUrl = typeof documentUrl === "string" && documentUrl.trim() !== "";
    if (selectedFile instanceof File || hasUrl) {
      form.clearErrors("file");
    }
  }, [selectedFile, documentUrl, form]);

  useEffect(() => {
    if (document && open) {
      const base = document.document;
      form.reset({
        documentType: base.documentType,
        description: base.description ?? "",
        documentUrl: base.documentUrl ?? "",
        file: undefined,
      });
      setFileInputKey((prev) => prev + 1);
    }
  }, [document, open, form]);

  useEffect(() => {
    if (!open) {
      form.reset({
        documentType: ReceivableDocumentType.INVOICE_FROM_PLATFORM,
        description: "",
        documentUrl: "",
        file: undefined,
      });
      setFileInputKey((prev) => prev + 1);
    }
  }, [open, form]);

  const handleSubmit = async (values: DocumentFormValues) => {
    if (disabled || isSubmitting || !document) return;
    await onSubmit(values);
  };

  const handleFileInputKeyChange = () => {
    setFileInputKey((prev) => prev + 1);
  };

  const currentFileName =
    document?.kind === "pending" && document.document.file
      ? document.document.file.name
      : undefined;

  const isBusy = disabled || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar documento</DialogTitle>
          <DialogDescription>
            Atualize as informações do documento ou envie um novo arquivo para substituir o atual.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(handleSubmit)(event);
            }}
            className="space-y-4"
          >
            <DocumentFormFields form={form} disabled={isBusy} />

            <DocumentFileUpload
              form={form}
              fileInputKey={fileInputKey}
              onFileInputKeyChange={handleFileInputKeyChange}
              disabled={isBusy}
              label="Substituir arquivo (opcional)"
              description="Envie um novo arquivo caso deseje substituir o existente."
              currentFileName={currentFileName}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isBusy} onClick={(e) => e.stopPropagation()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar alterações"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

